import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { ReactComponent as SendIconSvg } from "feather-icons/dist/icons/send.svg";
import { ReactComponent as SvgDecoratorBlob } from "images/svg-decorator-blob-9.svg";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";

/**
 * Section « Contact » de la page d’accueil (#8, key: contact-cta).
 *
 * Bandeau violet formulé comme un appel à contributions de l’écosystème
 * (signaler une actualité, un projet, un événement), cohérent avec le rôle de
 * média local. Recette de components/cta/GetStarted.js réécrite en palette
 * primary, avec des liens react-router (plus de bouton rouge, plus de <a href>).
 * Comme GetStarted, le Container ne porte aucune marge : ContentWithPaddingXl
 * assure seul le rythme vertical (py-20 lg:py-24).
 *
 * Aucune prop obligatoire ; les valeurs par défaut reprennent le texte de la spec.
 * `heading` accepte soit un ReactNode, soit une chaîne dont la partie entre
 * {{ }} est rendue avec HighlightedTextInverse (fond gris clair, texte violet).
 */

/* Variante inversée de HighlightedText, seule lisible sur fond primary-500. */
const HighlightedTextInverse = tw.span`bg-gray-100 text-primary-500 px-4 transform -skew-x-12 inline-block`;

const Wrap = Container;
const Band = tw.div`py-16 lg:py-20 bg-primary-500 rounded-lg relative`;
const BandRow = tw.div`px-8 max-w-screen-lg mx-auto flex items-center relative z-10 flex-col lg:flex-row text-center lg:text-left`;

const BandText = tw.div`lg:w-1/2 max-w-lg`;
/* Élément non-titre pour préserver l’ordre des niveaux de titres (h2 juste après). */
const BandSub = tw.p`text-gray-200 uppercase tracking-wider text-sm font-bold`;
/* h2 : un seul h1 sur la page (le hero), les autres sections sont en h2. */
const BandHeading = tw.h2`mt-2 text-gray-100 text-2xl sm:text-3xl font-bold leading-snug`;

const BandLinks = tw.div`w-full lg:w-1/2 max-w-lg flex justify-center lg:justify-end mt-6 lg:mt-0 flex-col sm:flex-row items-center sm:items-stretch`;
/* Focus clavier : l’anneau bleu de la DA (shadow-outline) est invisible sur fond violet ;
   double anneau gray-100 / primary-900, lisible sur primary-500. */
const BandLink = styled(Link)`
  ${tw`w-full sm:w-auto text-sm sm:text-base px-6 py-3 sm:px-8 sm:py-4 lg:px-10 lg:py-5 mt-4 first:mt-0 sm:mt-0 sm:mr-8 sm:last:mr-0 rounded-full font-bold border border-transparent tracking-wide transition duration-300 focus:outline-none text-center`}
  &:focus-visible {
    box-shadow: 0 0 0 3px #f7fafc, 0 0 0 6px #3c0d99;
  }
`;
const BandPrimary = tw(BandLink)`bg-gray-100 text-primary-500 shadow-lg hocus:bg-gray-200 hocus:text-primary-700`;
/* Pilule fantôme : bordure claire sur fond violet (même recette que blog/components/BlogCta.js). */
const BandSecondary = tw(BandLink)`text-gray-100 border-gray-100 hocus:bg-gray-100 hocus:text-primary-500`;
const SendIcon = tw(SendIconSvg)`w-4 h-4 mr-2 inline-block`;

const BandBlobs = tw.div`absolute inset-0 overflow-hidden rounded-lg pointer-events-none`;
const BandBlob1 = tw(SvgDecoratorBlob)`absolute bottom-0 left-0 w-80 h-80 transform -translate-x-20 translate-y-32 text-primary-700 opacity-50`;
const BandBlob2 = tw(SvgDecoratorBlob)`absolute top-0 right-0 w-80 h-80 transform translate-x-20 -translate-y-64 text-primary-700 opacity-50`;

/* Rend une chaîne « … {{mots mis en avant}} … » avec HighlightedTextInverse ;
   un ReactNode est rendu tel quel. */
const renderHeading = heading => {
  if (typeof heading !== "string") return heading;
  const parts = heading.split(/\{\{(.+?)\}\}/);
  return parts.map((part, i) =>
    i % 2 === 1 ? <HighlightedTextInverse key={i}>{part}</HighlightedTextInverse> : part
  );
};

export default function ContactCta({
  id = "contact",
  subheading = "Vous faites partie de l’écosystème ?",
  heading = "Signalez-nous une actualité, un projet, {{un événement.}}",
  ctaText = "Nous contacter",
  ctaUrl = "/contact",
  /* Second lien distinct du hero (« Découvrir le projet » → /about y figure déjà). */
  secondaryCtaText = "Lire le blog",
  secondaryCtaUrl = "/blog",
}) {
  return (
    <Wrap id={id}>
      <ContentWithPaddingXl>
        <Band>
          <BandRow>
            <BandText>
              {subheading && <BandSub>{subheading}</BandSub>}
              <BandHeading>{renderHeading(heading)}</BandHeading>
            </BandText>
            <BandLinks>
              <BandPrimary to={ctaUrl}>
                <SendIcon aria-hidden="true" focusable="false" />
                {ctaText}
              </BandPrimary>
              {secondaryCtaText && secondaryCtaUrl && (
                <BandSecondary to={secondaryCtaUrl}>{secondaryCtaText}</BandSecondary>
              )}
            </BandLinks>
          </BandRow>
          <BandBlobs aria-hidden="true">
            <BandBlob1 focusable="false" />
            <BandBlob2 focusable="false" />
          </BandBlobs>
        </Band>
      </ContentWithPaddingXl>
    </Wrap>
  );
}
